import React, { useState, useEffect } from 'react';

import {
  Container,
  Form,
  Button,
  Alert,
  Spinner,
  Image,
  Modal,
  Badge,
} from 'react-bootstrap';

import { useParams, useNavigate } from 'react-router-dom';

import api from '../api';

import ConditionNoteTemplates from '../components/ConditionNoteTemplates';


function DraftForm() {
  const { id } = useParams();

  const navigate = useNavigate();


  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    cost: '',
    price: '',
    stock: '',
    condition_notes: '',
    notes: '',
    collections: [],
    length: '',
    width: '',
    height: '',
    weight: '',
    ounces: '',
    platform: '',
    binLocation: '',
    created_at: '',
  });


  const [images, setImages] = useState([]);

  const [existingImages, setExistingImages] = useState([]);


  const [collectionOptions, setCollectionOptions] = useState([]);

  const [loadingCollections, setLoadingCollections] = useState(false);

  const [creatingCollection, setCreatingCollection] = useState(false);


  // --------------------------------------------------
  // Purchase Batch state
  // --------------------------------------------------

  const [purchaseBatches, setPurchaseBatches] = useState([]);

  const [selectedBatchId, setSelectedBatchId] = useState('');

  const [originalBatchId, setOriginalBatchId] = useState('');

  const [originalSku, setOriginalSku] = useState('');

  const [loadingBatches, setLoadingBatches] = useState(false);


  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);


  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const [discardReason, setDiscardReason] = useState('');


  const isEdit = !!id;


  useEffect(() => {
    fetchCollectionOptions();

    fetchPurchaseBatches();

    if (id) {
      fetchDraft();
    }
  }, [id]);


  const parseCollections = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((collection) => String(collection || '').trim())
        .filter(Boolean);
    }

    if (
      value === undefined ||
      value === null ||
      value === ''
    ) {
      return [];
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (!trimmed) {
        return [];
      }

      try {
        const parsed = JSON.parse(trimmed);

        if (Array.isArray(parsed)) {
          return parsed
            .map((collection) => String(collection || '').trim())
            .filter(Boolean);
        }
      } catch (parseError) {
        console.warn(
          '[DraftForm] Collections were not valid JSON. Falling back to comma-separated values:',
          parseError
        );
      }

      return trimmed
        .split(',')
        .map((collection) => collection.trim())
        .filter(Boolean);
    }

    return [];
  };


  const parseImages = (value) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    if (!value) {
      return [];
    }

    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        console.log('[DraftForm] Parsed images:', parsed);

        return parsed;
      }

      return [];
    } catch (parseError) {
      console.warn(
        '[DraftForm] Invalid images JSON:',
        value,
        parseError
      );

      return [];
    }
  };


  const fetchCollectionOptions = async () => {
    try {
      setLoadingCollections(true);

      const response = await api.get('/drafts/collections');

      const options = Array.isArray(response.data?.collections)
        ? response.data.collections
        : [];

      setCollectionOptions(options);

      console.log(
        '[DraftForm] Loaded collection options:',
        options
      );
    } catch (collectionError) {
      console.error(
        '[DraftForm] Failed to load collection options:',
        collectionError.response?.data || collectionError.message
      );

      setError(
        collectionError.response?.data?.error ||
          'Failed to load collection buttons.'
      );

      if (collectionError.response?.status === 401) {
        localStorage.removeItem('token');

        navigate('/login');
      }
    } finally {
      setLoadingCollections(false);
    }
  };


  // --------------------------------------------------
  // Load Purchase Batches
  // --------------------------------------------------

  const fetchPurchaseBatches = async () => {
    try {
      setLoadingBatches(true);

      const response = await api.get('/purchase-batches');

      const batches = Array.isArray(response.data?.batches)
        ? response.data.batches
        : [];

      setPurchaseBatches(batches);

      console.log(
        '[DraftForm] Loaded purchase batches:',
        batches
      );
    } catch (batchError) {
      console.error(
        '[DraftForm] Failed to load purchase batches:',
        batchError.response?.data || batchError.message
      );

      setError(
        batchError.response?.data?.error ||
          'Failed to load purchase batches.'
      );

      if (batchError.response?.status === 401) {
        localStorage.removeItem('token');

        navigate('/login');
      }
    } finally {
      setLoadingBatches(false);
    }
  };


  const fetchDraft = async () => {
    setLoading(true);

    try {
      const response = await api.get(`/drafts/${id}`);

      const draft = response.data;

      console.log('[DraftForm] Fetched draft:', draft);


      // --------------------------------------------------
      // Remember original SKU
      // --------------------------------------------------

      setOriginalSku(draft.sku || '');


      // --------------------------------------------------
      // Look up existing Purchase Batch by SKU
      // --------------------------------------------------

      if (draft.sku) {
        try {
          const batchResponse = await api.get(
            `/purchase-batches/by-sku/${encodeURIComponent(draft.sku)}`
          );

          const batchItem = batchResponse.data?.item;

          if (batchItem?.batch_id) {
            setSelectedBatchId(String(batchItem.batch_id));

            setOriginalBatchId(String(batchItem.batch_id));
          } else {
            setSelectedBatchId('');

            setOriginalBatchId('');
          }
        } catch (batchLookupError) {
          console.error(
            '[DraftForm] Failed to look up purchase batch:',
            batchLookupError.response?.data ||
              batchLookupError.message
          );

          setSelectedBatchId('');

          setOriginalBatchId('');
        }
      }


      setFormData({
        sku: draft.sku || '',

        name: draft.name || '',

        cost:
          draft.cost !== null &&
          draft.cost !== undefined
            ? draft.cost.toString()
            : '',

        price:
          draft.price !== null &&
          draft.price !== undefined
            ? draft.price.toString()
            : '',

        stock:
          draft.stock !== null &&
          draft.stock !== undefined
            ? draft.stock.toString()
            : '',

        condition_notes: draft.condition_notes || '',

        notes: draft.notes || '',

        collections: parseCollections(draft.collections),

        length:
          draft.length !== null &&
          draft.length !== undefined
            ? draft.length.toString()
            : '',

        width:
          draft.width !== null &&
          draft.width !== undefined
            ? draft.width.toString()
            : '',

        height:
          draft.height !== null &&
          draft.height !== undefined
            ? draft.height.toString()
            : '',

        weight:
          draft.weight !== null &&
          draft.weight !== undefined
            ? draft.weight.toString()
            : '',

        ounces:
          draft.ounces !== null &&
          draft.ounces !== undefined
            ? draft.ounces.toString()
            : '',

        platform: draft.platform || '',

        binLocation:
          draft.binLocation ||
          draft.bin_location ||
          draft.bin_name ||
          '',

        created_at: draft.created_at || '',
      });


      const parsedImages = parseImages(draft.images);

      console.log(
        '[DraftForm] Parsed existing images:',
        parsedImages
      );

      setExistingImages(parsedImages);
    } catch (draftError) {
      console.error(
        '[DraftForm] Error fetching draft:',
        draftError
      );

      setError('Failed to load draft. Please try again.');

      if (draftError.response?.status === 401) {
        localStorage.removeItem('token');

        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };


  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,

      [name]: value,
    }));
  };


  const handleImageChange = (event) => {
    const files = Array.from(event.target.files || []);

    console.log('[DraftForm] Selected images:', files);

    setImages(files);
  };


  const toggleCollection = (collectionName) => {
    const cleanName = String(collectionName || '').trim();

    if (!cleanName) {
      return;
    }

    setFormData((previous) => {
      const currentCollections = Array.isArray(previous.collections)
        ? previous.collections
        : [];

      const alreadySelected = currentCollections.some(
        (collection) =>
          collection.toLowerCase() === cleanName.toLowerCase()
      );

      return {
        ...previous,

        collections: alreadySelected
          ? currentCollections.filter(
              (collection) =>
                collection.toLowerCase() !== cleanName.toLowerCase()
            )
          : [...currentCollections, cleanName],
      };
    });
  };


  const createNewCollection = async () => {
    const enteredName = window.prompt(
      'Enter the new collection name:'
    );

    if (enteredName === null) {
      return;
    }

    const cleanName = enteredName.trim().replace(/\s+/g, ' ');

    if (!cleanName) {
      alert('Collection name is required.');

      return;
    }

    if (cleanName.length > 100) {
      alert('Collection name cannot exceed 100 characters.');

      return;
    }

    try {
      setCreatingCollection(true);

      const response = await api.post('/drafts/collections', {
        name: cleanName,
      });

      const savedCollection =
        response.data?.collection || {
          id: `temporary-${Date.now()}`,

          name: cleanName,

          is_active: 1,
        };

      setCollectionOptions((previous) => {
        const alreadyExists = previous.some(
          (collection) =>
            String(collection.name || '').toLowerCase() ===
            cleanName.toLowerCase()
        );

        if (alreadyExists) {
          return previous.map((collection) =>
            String(collection.name || '').toLowerCase() ===
            cleanName.toLowerCase()
              ? savedCollection
              : collection
          );
        }

        return [...previous, savedCollection].sort((a, b) =>
          String(a.name || '').localeCompare(String(b.name || ''))
        );
      });

      setFormData((previous) => {
        const currentCollections = Array.isArray(previous.collections)
          ? previous.collections
          : [];

        const alreadySelected = currentCollections.some(
          (collection) =>
            collection.toLowerCase() === cleanName.toLowerCase()
        );

        return {
          ...previous,

          collections: alreadySelected
            ? currentCollections
            : [...currentCollections, cleanName],
        };
      });

      alert(`${cleanName} was added as a collection button.`);
    } catch (collectionError) {
      console.error(
        '[DraftForm] Error creating collection:',
        collectionError.response?.data || collectionError.message
      );

      alert(
        collectionError.response?.data?.error ||
          collectionError.response?.data?.details ||
          collectionError.message ||
          'Failed to create collection.'
      );

      if (collectionError.response?.status === 401) {
        localStorage.removeItem('token');

        navigate('/login');
      }
    } finally {
      setCreatingCollection(false);
    }
  };


  const handleDiscard = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await api.post(
        `/discards/${id}`,

        {
          reason: discardReason,
        },

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert(
        `Draft discarded: ${response.data.sku} (Cost: $${response.data.cost})`
      );

      setShowDiscardModal(false);

      setDiscardReason('');

      navigate('/inventory/drafts');
    } catch (discardError) {
      console.error(
        '[DraftForm] Error discarding draft:',
        discardError
      );

      alert('Failed to discard draft');
    }
  };


  const generateSKU = async () => {
    setLoading(true);

    try {
      const response = await api.get('/generate-sku');

      setFormData((previous) => ({
        ...previous,

        sku: response.data.sku,
      }));
    } catch (skuError) {
      console.error(
        '[DraftForm] Error generating SKU:',
        skuError
      );

      setError('Failed to generate SKU.');

      if (skuError.response?.status === 401) {
        localStorage.removeItem('token');

        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    setLoading(true);

    setError(null);

    try {
      const data = new FormData();


      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'collections') {
          data.append(
            'collections',

            JSON.stringify(
              Array.isArray(value) ? value : []
            )
          );

          return;
        }

        data.append(
          key,

          value !== undefined && value !== null
            ? value
            : ''
        );
      });


      images.forEach((image) => {
        data.append('images', image);
      });


      if (isEdit && existingImages.length > 0) {
        data.append(
          'images',

          JSON.stringify(existingImages)
        );
      }


      console.log('[DraftForm] Submitting FormData:', {
        ...formData,

        collections: formData.collections,

        newImages: images.length,

        existingImages,
      });


      const token = localStorage.getItem('token');


      // --------------------------------------------------
      // Existing normal Draft save
      // --------------------------------------------------

      const response = isEdit
        ? await api.put(`/drafts/${id}`, data, {
            headers: {
              'Content-Type': 'multipart/form-data',

              Authorization: `Bearer ${token}`,
            },
          })
        : await api.post('/drafts', data, {
            headers: {
              'Content-Type': 'multipart/form-data',

              Authorization: `Bearer ${token}`,
            },
          });


      console.log(
        '[DraftForm] API response:',

        response.data
      );


      // --------------------------------------------------
      // Purchase Batch assignment
      //
      // This remains separate from the normal draft save.
      // --------------------------------------------------

      const savedSku = String(formData.sku || '').trim();


      if (savedSku) {
        // --------------------------------------------------
        // If an existing draft's SKU changed,
        // remove the old SKU's batch relationship first.
        // --------------------------------------------------

        if (
          isEdit &&
          originalSku &&
          originalSku !== savedSku &&
          originalBatchId
        ) {
          await api.delete(
            `/purchase-batches/item/${encodeURIComponent(
              originalSku
            )}`
          );
        }


        // --------------------------------------------------
        // Assign selected Purchase Batch
        // --------------------------------------------------

        if (selectedBatchId) {
          // Only make an assignment request when:
          //
          // 1. This is a new draft
          // 2. The selected batch changed
          // 3. The SKU changed
          //
          // This prevents an unchanged finalized batch
          // from being unnecessarily re-assigned.
          if (
            !isEdit ||
            String(selectedBatchId) !== String(originalBatchId) ||
            originalSku !== savedSku
          ) {
            await api.post('/purchase-batches/assign', {
              batch_id: Number(selectedBatchId),

              sku: savedSku,
            });

            console.log(
              `[DraftForm] Assigned ${savedSku} to purchase batch ${selectedBatchId}`
            );
          }
        }

        // --------------------------------------------------
        // Remove batch assignment if None was selected
        // --------------------------------------------------

        else if (isEdit && originalBatchId) {
          await api.delete(
            `/purchase-batches/item/${encodeURIComponent(
              savedSku
            )}`
          );

          console.log(
            `[DraftForm] Removed ${savedSku} from purchase batch`
          );
        }
      }


      navigate('/inventory/drafts');
    } catch (saveError) {
      console.error(
        '[DraftForm] Error saving draft:',

        saveError
      );

      setError(
        `Failed to save draft: ${
          saveError.response?.data?.error ||
          saveError.message
        }`
      );

      if (saveError.response?.status === 401) {
        localStorage.removeItem('token');

        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };


  const getImageUrl = (image) => {
    if (!image) {
      return 'No Image';
    }

    const baseUrl =
      process.env.REACT_APP_API_URL ||
      'https://thornsdashboard.com';

    const normalizedImage = image.startsWith('/')
      ? image.slice(1)
      : image;

    const url = normalizedImage.startsWith('http')
      ? normalizedImage
      : `${baseUrl}/${normalizedImage}`;

    console.log('[DraftForm] Image URL:', url);

    return url;
  };


  return (
    <Container className="mt-4">

      <h2 className="fw-bold">
        {isEdit ? 'Edit Draft' : 'Create New Draft'}
      </h2>


      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}


      {loading && (
        <Spinner
          animation="border"
          className="d-block mx-auto my-3"
        />
      )}


      <Form onSubmit={handleSubmit}>

        <Form.Group className="mb-3">

          <Form.Label>SKU</Form.Label>

          <div className="d-flex gap-2">

            <Form.Control
              type="text"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              required
            />

            <Button
              variant="secondary"
              type="button"
              onClick={generateSKU}
              disabled={loading}
            >
              Generate SKU
            </Button>

          </div>

        </Form.Group>


        {/* --------------------------------------------------
            Purchase Batch
        -------------------------------------------------- */}

        <Form.Group className="mb-3">

          <Form.Label>Purchase Batch</Form.Label>

          <Form.Select
            value={selectedBatchId}
            onChange={(event) =>
              setSelectedBatchId(event.target.value)
            }
            disabled={loadingBatches}
          >

            <option value="">
              None
            </option>

            {purchaseBatches
              .filter(
                (batch) =>
                  batch.status === 'OPEN' ||
                  String(batch.id) ===
                    String(selectedBatchId)
              )
              .map((batch) => (
                <option
                  key={batch.id}
                  value={batch.id}
                >
                  {batch.name}
                  {batch.status === 'FINALIZED'
                    ? ' (Finalized)'
                    : ''}
                </option>
              ))}

          </Form.Select>

          <Form.Text className="text-muted">
            Assign this SKU to a purchase batch such as a garage sale,
            estate sale, or bulk purchase.
          </Form.Text>

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Date Created</Form.Label>

          <Form.Control
            type="text"
            value={
              formData.created_at
                ? new Date(
                    formData.created_at
                  ).toLocaleString()
                : ''
            }
            readOnly
          />

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Name</Form.Label>

          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Optional product name"
          />

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Cost ($)</Form.Label>

          <Form.Control
            type="number"
            name="cost"
            value={formData.cost}
            onChange={handleChange}
            step="0.01"
            min="0"
          />

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Price ($)</Form.Label>

          <Form.Control
            type="number"
            name="price"
            value={formData.price}
            onChange={handleChange}
            step="0.01"
            min="0"
          />

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Stock</Form.Label>

          <Form.Control
            type="number"
            name="stock"
            value={formData.stock}
            onChange={handleChange}
            min="0"
          />

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Condition Notes</Form.Label>

          <ConditionNoteTemplates
            setFormData={setFormData}
            formData={formData}
          />

          <Form.Control
            as="textarea"
            name="condition_notes"
            value={formData.condition_notes}
            onChange={handleChange}
            rows={4}
          />

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Notes</Form.Label>

          <Form.Control
            as="textarea"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Internal notes about this draft..."
          />

          <Form.Text className="text-muted">
            These notes are saved with the draft separately from the condition notes.
          </Form.Text>

        </Form.Group>


        <Form.Group className="mb-4">

          <div className="d-flex justify-content-between align-items-center mb-2">

            <Form.Label className="mb-0">
              Collections
            </Form.Label>

            <Button
              type="button"
              variant="outline-success"
              size="sm"
              onClick={createNewCollection}
              disabled={creatingCollection}
            >
              {creatingCollection
                ? 'Creating...'
                : '+ Create New Collection'}
            </Button>

          </div>


          <div className="border rounded p-3">

            {loadingCollections ? (

              <div className="text-muted">

                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />

                Loading collections...

              </div>

            ) : collectionOptions.length === 0 ? (

              <Alert
                variant="secondary"
                className="mb-0"
              >
                No collection buttons have been created yet. Click
                <strong> Create New Collection</strong> to add one.
              </Alert>

            ) : (

              <div className="d-flex flex-wrap gap-2">

                {collectionOptions.map((collection) => {
                  const collectionName = String(
                    collection.name || ''
                  ).trim();

                  const isSelected =
                    formData.collections.some(
                      (selectedCollection) =>
                        selectedCollection.toLowerCase() ===
                        collectionName.toLowerCase()
                    );

                  return (
                    <Button
                      key={
                        collection.id ||
                        collectionName
                      }
                      type="button"
                      variant={
                        isSelected
                          ? 'success'
                          : 'outline-secondary'
                      }
                      onClick={() =>
                        toggleCollection(collectionName)
                      }
                    >
                      {isSelected && '✓ '}
                      {collectionName}
                    </Button>
                  );
                })}

              </div>

            )}


            {formData.collections.length > 0 && (

              <div className="mt-3">

                <div className="fw-bold mb-2">
                  Selected Collections
                </div>

                <div className="d-flex flex-wrap gap-2">

                  {formData.collections.map(
                    (collection) => (

                      <Badge
                        key={collection}
                        bg="success"
                        className="p-2"
                        style={{
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          toggleCollection(collection)
                        }
                        title="Click to remove"
                      >
                        {collection} ×
                      </Badge>

                    )
                  )}

                </div>

              </div>

            )}


            <Form.Text className="text-muted d-block mt-2">
              Tap one or more collection buttons to select them. Selected collections are highlighted in green.
            </Form.Text>

          </div>

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Photos</Form.Label>

          <Form.Control
            type="file"
            name="images"
            multiple
            accept="image/*"
            capture="camera"
            onChange={handleImageChange}
          />


          {existingImages.length > 0 && (

            <div className="mt-2">

              <h5>Existing Images</h5>

              <div className="d-flex flex-wrap gap-2">

                {existingImages.map((image, index) => (

                  <Image
                    key={`${image}-${index}`}
                    src={getImageUrl(image)}
                    alt="Draft"
                    width="100"
                    height="100"
                    style={{
                      objectFit: 'cover',
                    }}
                    onError={(event) => {
                      console.error(
                        '[DraftForm] Image load error:',
                        image
                      );

                      event.target.src =
                        'https://via.placeholder.com/100?text=Error';
                    }}
                  />

                ))}

              </div>

            </div>

          )}

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Length (in)</Form.Label>

          <Form.Control
            type="number"
            name="length"
            value={formData.length}
            onChange={handleChange}
            step="0.01"
            min="0"
          />

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Width (in)</Form.Label>

          <Form.Control
            type="number"
            name="width"
            value={formData.width}
            onChange={handleChange}
            step="0.01"
            min="0"
          />

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Height (in)</Form.Label>

          <Form.Control
            type="number"
            name="height"
            value={formData.height}
            onChange={handleChange}
            step="0.01"
            min="0"
          />

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Weight</Form.Label>

          <div className="d-flex gap-2">

            <Form.Control
              type="number"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              placeholder="lb"
              min="0"
            />

            <Form.Control
              type="number"
              name="ounces"
              value={formData.ounces}
              onChange={handleChange}
              placeholder="oz"
              min="0"
            />

          </div>

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Platform</Form.Label>

          <Form.Select
            name="platform"
            value={formData.platform}
            onChange={handleChange}
          >

            <option value="">
              Select Platform
            </option>

            <option value="eBay">
              eBay
            </option>

            <option value="Shopify">
              Shopify
            </option>

            <option value="Walmart">
              Walmart
            </option>

          </Form.Select>

        </Form.Group>


        <Form.Group className="mb-3">

          <Form.Label>Bin Location</Form.Label>

          <Form.Control
            type="text"
            name="binLocation"
            value={formData.binLocation}
            onChange={handleChange}
            placeholder="e.g., A-12"
          />

        </Form.Group>


        <Button
          variant="primary"
          type="submit"
          disabled={loading}
        >
          {isEdit
            ? 'Update Draft'
            : 'Create Draft'}
        </Button>


        {isEdit && (

          <Button
            variant="danger"
            type="button"
            className="ms-2"
            onClick={() =>
              setShowDiscardModal(true)
            }
          >
            Discard Draft
          </Button>

        )}

      </Form>


      <Modal
        show={showDiscardModal}
        onHide={() =>
          setShowDiscardModal(false)
        }
      >

        <Modal.Header closeButton>

          <Modal.Title>
            Discard Draft
          </Modal.Title>

        </Modal.Header>


        <Modal.Body>

          <Form.Group>

            <Form.Label>Reason</Form.Label>

            <Form.Control
              as="textarea"
              rows={3}
              value={discardReason}
              onChange={(event) =>
                setDiscardReason(
                  event.target.value
                )
              }
              placeholder="e.g. Broken, missing parts, failed inspection"
            />

          </Form.Group>

        </Modal.Body>


        <Modal.Footer>

          <Button
            variant="secondary"
            type="button"
            onClick={() =>
              setShowDiscardModal(false)
            }
          >
            Cancel
          </Button>


          <Button
            variant="danger"
            type="button"
            onClick={handleDiscard}
          >
            Confirm Discard
          </Button>

        </Modal.Footer>

      </Modal>

    </Container>
  );
}


export default DraftForm;